import type { IBuildingCard } from 'shared/types/buildingCard';
import { getBuildingName } from 'shared/utils/buildingMainBlock';

export interface IBuildingSeoMeta {
  title: string;
  description: string;
  keywords: string;
}

/** Тип ТЦ и склада в SEO-текстах: сокращение и полное название в предложном падеже */
interface ISeoBuildingType {
  short: string;
  full: string;
}

/** Адресообразующие элементы, которые попадают в SEO-тексты (`_address` монолита) */
const SEO_ADDRESS_TYPES = new Set(['Location', 'Street', 'House']);

/** Слово и имя через пробел; без имени — только слово (монолит подставлял `None`) */
const withName = (words: string, name: string): string => (name ? `${words} ${name}` : words);

/** Части через «, » без пустых — без висячих запятых при пустом имени или адресе */
const joinParts = (...parts: string[]): string => parts.filter(Boolean).join(', ');

/**
 * Тип ТЦ и склада — `_building_type` SEO-презентера монолита. БЦ в шаблонах пишется словами («БЦ», «Бизнес Центре»),
 * нештатный тип — как ТЦ, как в ветке «не БЦ» монолита
 */
export function getSeoBuildingType(card: IBuildingCard): ISeoBuildingType {
  if (card.type === 'warehouse') {
    return { short: 'СК', full: 'Складском комплексе' };
  }
  if (card.buildingType === 'shoppingAndEntertainmentCenter') {
    return { short: 'ТРЦ', full: 'Торгово-развлекательном центре' };
  }

  return { short: 'ТЦ', full: 'Торговом центре' };
}

/** Адрес SEO-текстов: город, улица и дом из адресообразующих элементов (`_address` монолита) */
export function getSeoAddress(card: IBuildingCard): string {
  return card.address
    .filter(
      ({ isFormingAddress, type, shortName }) => isFormingAddress && type && SEO_ADDRESS_TYPES.has(type) && shortName,
    )
    .map(({ shortName }) => shortName)
    .join(', ');
}

/**
 * title, description и keywords — шаблоны `BusinessShoppingCenterSeoPresenter` монолита. Тексты не меняем, чтобы
 * не ломать индексацию; убираем только пустые части. `city` — город поддомена, без него описание кончается на «ЦИАН»
 */
export function getBuildingSeoMeta(card: IBuildingCard, city: string | null): IBuildingSeoMeta {
  const name = getBuildingName(card);
  const address = getSeoAddress(card);
  const base = withName('база коммерческой недвижимости', city ?? '');

  if (card.type === 'businessCenter') {
    return {
      title:
        `${withName('БЦ', name)} – аренда и продажа помещений, офисов ` +
        `${joinParts(withName('в Бизнес Центре', name), address)} – Коммерческая недвижимость ЦИАН`,
      description:
        `Объявления об аренде и продаже помещений ${joinParts(withName('в БЦ', name), address)}: ` +
        `описание объектов, фото, контакты продавцов – ${base} ЦИАН`,
      keywords: `${withName('БЦ', name)}, помещение, офис, аренда, продажа`,
    };
  }

  const { short, full } = getSeoBuildingType(card);

  return {
    title:
      `${withName(short, name)} – аренда и продажа торговых помещений ` +
      `${joinParts(withName(`в ${full}`, name), address)} – Коммерческая недвижимость ЦИАН`,
    description:
      `Объявления об аренде и продаже помещений ${joinParts(withName(`в ${short}`, name), address)}: ` +
      `описание объектов, фото, контакты продавцов – ${base} ЦИАН`,
    keywords: `${withName(short, name)}, помещение, офис, аренда, продажа`,
  };
}

/**
 * alt фото галереи — шаблоны `photos_with_alt` монолита. В монолите шаблон выбирался случайно на каждый запрос;
 * у нас — по индексу фото по кругу, чтобы alt не менялся между показами
 */
export function getPhotoAlts(card: IBuildingCard): string[] {
  const name = getBuildingName(card);
  const { short, full } = getSeoBuildingType(card);
  const templates =
    card.type === 'businessCenter'
      ? ['БЦ', 'аренда помещений в БЦ', 'продажа помещений в БЦ', 'Бизнес Центр']
      : [short, `аренда помещений в ${short}`, `продажа помещений в ${short}`, full];

  return card.photos.map((_, index) => withName(templates[index % templates.length], name));
}
